import { backendConfigured, supabase } from "./auth.ts";
import { meta, setMeta } from "./db/idb.ts";

/**
 * Inviting a second member (FR-4.3).
 *
 * The database does the load-bearing part: `create_invitation()` returns the
 * raw token exactly once and stores only its SHA-256, `redeem_invitation()`
 * is single-use, seven days, and fails closed. Nothing here can weaken any of
 * that — this file only carries the token between two people and hands it
 * back to the server.
 *
 * The token rides in the URL fragment, not the query string. A fragment is
 * never sent to the host, so it stays out of Cloudflare's logs, out of the
 * Referer header, and out of the service worker's cache keys. It is stripped
 * from the address bar the moment it is read, so a screenshot or a shared
 * "look at this app" link does not carry a working invitation with it.
 */

/** `encode(gen_random_bytes(24), 'hex')` — 48 hex characters, nothing else. */
const TOKEN = /^[0-9a-f]{48}$/;
const PARAM = "kutsu";
const PENDING = "pending_invite";

export type Role = "OWNER" | "MEMBER";

export function tokenFromUrl(href: string): string | null {
  let u: URL;
  try { u = new URL(href); } catch { return null; }
  const body = u.hash.replace(/^#/, "");
  const inHash = body.includes("=") ? new URLSearchParams(body).get(PARAM) : null;
  const raw = (inHash ?? u.searchParams.get(PARAM) ?? "").trim().toLowerCase();
  return TOKEN.test(raw) ? raw : null;
}

/**
 * The same address with the token taken out and everything else left alone —
 * Supabase's own hash parameters included, because the client library reads
 * them a moment later and a session that never lands is a sign-in that fails
 * for no visible reason.
 */
export function urlWithoutToken(href: string): string {
  let u: URL;
  try { u = new URL(href); } catch { return href; }
  const body = u.hash.replace(/^#/, "");
  if (body.includes("=")) {
    const p = new URLSearchParams(body);
    if (p.has(PARAM)) {
      p.delete(PARAM);
      const rest = p.toString();
      u.hash = rest ? `#${rest}` : "";
    }
  }
  u.searchParams.delete(PARAM);
  return u.toString();
}

export function inviteLink(token: string, base?: string): string {
  const root = base ?? `${location.origin}/`;
  return `${root.replace(/\/+$/, "")}/#${PARAM}=${token}`;
}

/**
 * Read a token out of the address, remember it, and clean the address.
 *
 * It is remembered rather than redeemed on the spot because the person who
 * opened the link usually has no account yet: they will create one, and on a
 * phone that may mean the app is reopened from the home screen in between.
 * A token in IndexedDB survives that; one held in a variable does not.
 *
 * With no token in the address, whatever was stored earlier is returned — so
 * the sign-in screen can say what the person is signing in *for*.
 */
export async function capturePendingInvite(
  href: string = typeof location === "undefined" ? "" : location.href,
  replace: (url: string) => void = (url) => {
    try { history.replaceState(null, "", url); } catch { /* not fatal */ }
  },
): Promise<string | null> {
  const token = tokenFromUrl(href);
  if (!token) return (await pendingInvite());
  await setMeta(PENDING, token);
  replace(urlWithoutToken(href));
  return token;
}

export async function pendingInvite(): Promise<string | null> {
  return (await meta<string>(PENDING)) ?? null;
}

export async function clearPendingInvite(): Promise<void> {
  await setMeta(PENDING, null);
}

export interface RedeemResult {
  /** The sauna joined, when it worked. */
  sauna_id: string | null;
  /** Something to show the person, when it will not work and never will. */
  error: string | null;
  /** True when the token is still worth trying later — a lost network. */
  retry: boolean;
}

/**
 * Redeem whatever is pending, if anything.
 *
 * This has to run before the first sync of the session. `SyncEngine` pushes
 * the device's own local sauna to the server when the account has none, so a
 * redeem that comes second gives an invited person an empty sauna of their
 * own and leaves them in it — the exact failure the invitation was for.
 */
export async function redeemPendingInvite(): Promise<RedeemResult | null> {
  const token = await pendingInvite();
  if (!token || !backendConfigured()) return null;
  try {
    const { data, error } = await supabase().rpc("redeem_invitation", { p_token: token });
    if (error) throw new Error(error.message);
    await clearPendingInvite();
    return { sauna_id: String(data), error: null, retry: false };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // A used, revoked or expired token will not become valid by waiting, and
    // keeping it would re-run this on every boot for ever.
    if (isNetwork(msg)) return { sauna_id: null, error: null, retry: true };
    await clearPendingInvite();
    return { sauna_id: null, error: explain(msg), retry: false };
  }
}

export async function createInvitation(sauna_id: string, role: Role = "MEMBER"):
  Promise<{ token: string; url: string }> {
  const { data, error } = await supabase()
    .rpc("create_invitation", { p_sauna: sauna_id, p_role: role });
  if (error) throw new Error(explain(error.message));
  const token = String(data);
  return { token, url: inviteLink(token) };
}

export interface InviteRow {
  id: string; role: Role;
  created_at: string; expires_at: string;
  revoked_at: string | null; used_at: string | null;
  used_by_name: string | null;
}

/** FR-4.3b: which links are outstanding, and which account consumed each. */
export async function listInvitations(sauna_id: string): Promise<InviteRow[]> {
  const sb = supabase();
  const { data, error } = await sb.from("invitation")
    .select("id, role, created_at, expires_at, revoked_at, used_at, used_by")
    .eq("sauna_id", sauna_id)
    .order("created_at", { ascending: false });
  if (error) throw new Error(explain(error.message));
  const rows = (data ?? []) as Record<string, unknown>[];
  const names = await displayNames(rows.map((r) => r["used_by"] as string | null));
  return rows.map((r) => ({
    id: r["id"] as string,
    role: r["role"] as Role,
    created_at: r["created_at"] as string,
    expires_at: r["expires_at"] as string,
    revoked_at: (r["revoked_at"] as string) ?? null,
    used_at: (r["used_at"] as string) ?? null,
    used_by_name: names.get(r["used_by"] as string) ?? null,
  }));
}

export async function revokeInvitation(id: string): Promise<void> {
  const { error } = await supabase().from("invitation")
    .update({ revoked_at: new Date().toISOString() }).eq("id", id);
  if (error) throw new Error(explain(error.message));
}

export interface MemberRow { user_id: string; role: Role; name: string; joined_at: string }

export async function listMembers(sauna_id: string): Promise<MemberRow[]> {
  const { data, error } = await supabase().from("membership")
    .select("user_id, role, joined_at")
    .eq("sauna_id", sauna_id)
    .is("removed_at", null);
  if (error) throw new Error(explain(error.message));
  const rows = (data ?? []) as Record<string, unknown>[];
  const names = await displayNames(rows.map((r) => r["user_id"] as string));
  return rows
    .map((r) => ({
      user_id: r["user_id"] as string,
      role: r["role"] as Role,
      joined_at: r["joined_at"] as string,
      name: names.get(r["user_id"] as string) ?? "—",
    }))
    .sort((a, b) => (a.role === b.role ? a.joined_at.localeCompare(b.joined_at)
                                       : a.role === "OWNER" ? -1 : 1));
}

/**
 * Names for a set of accounts. A separate query rather than an embedded
 * one: the join through PostgREST depends on how it names the relationship,
 * and this asks a question that cannot be misread. RLS answers only for
 * people who share a sauna with the asker, which is exactly the set here.
 */
async function displayNames(ids: (string | null)[]): Promise<Map<string, string>> {
  const want = [...new Set(ids.filter((x): x is string => !!x))];
  const out = new Map<string, string>();
  if (!want.length) return out;
  const { data, error } = await supabase().from("app_user")
    .select("id, display_name, pseudonym, deleted_at").in("id", want);
  if (error) return out;                 // a missing name is not worth a failure
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    const closed = !!r["deleted_at"];
    out.set(r["id"] as string,
      (closed ? (r["pseudonym"] as string) : (r["display_name"] as string)) ?? "—");
  }
  return out;
}

const isNetwork = (m: string) =>
  /failed to fetch|networkerror|load failed|network request failed|timeout|aborted/i.test(m);

/** The server answers in English, and its words are the only useful part. */
export function explain(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("already used")) return "Kutsu on jo käytetty.";
  if (m.includes("revoked")) return "Kutsu on peruutettu.";
  if (m.includes("expired")) return "Kutsu on vanhentunut.";
  if (m.includes("invalid invitation")) return "Kutsulinkki ei kelpaa.";
  if (m.includes("sign in first")) return "Kirjaudu ensin sisään.";
  if (m.includes("only an owner")) return "Vain saunan omistaja voi kutsua jäseniä.";
  if (m.includes("row-level security")) return "Palvelin ei antanut oikeutta tähän.";
  if (isNetwork(m)) return "Ei saatu yhteyttä palvelimeen.";
  return message;
}
